import { navigate, PageProps } from 'gatsby';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Header, Icon, MenuButton } from '@opswat/ui';
import { PageHeader } from 'components/layout/PageHeader';
import { Footer } from 'components/footer/Footer';
import { useUser } from 'services/User';
import { canAccessHomePage, canAccessPage } from 'services/accessControl';

type PublicLayoutProps = Omit<PageProps, 'children'> & { children?: ReactNode; };

export const PublicLayout = ({ children, location }: PublicLayoutProps) => {
    const pathname = location?.pathname;
    const { user } = useUser();
    const { t } = useTranslation();

    const navButtons = [
        { text: t('Product'), onClick: () => navigate('/product') },
        { text: t('Integrations'), onClick: () => navigate('/integrations/all'), },
        { text: t('Blog'), onClick: () => navigate('/blog') },
        { text: t('Licensing'), onClick: () => navigate('/licensing') },
        ...(canAccessPage(user, 'store') ? [{ text: t('Store'), onClick: () => navigate('/store') }] : []),
    ];

    return (
        <div className='tw:w-screen tw:h-screen tw:flex tw:flex-col'>
            <header
                className='tw:flex tw:flex-row tw:justify-between tw:items-center tw:w-full tw:p-20 tw:border-b tw:border-neutrals-200 tw:dark:border-neutrals-1000'
                role='banner'
            >
                <Header.Product productSubtitle='Cloud' />
                <div className='tw:flex tw:flex-row tw:items-center tw:gap-8'>
                    {navButtons.map(props => (
                        <Button
                            key={props.text}
                            variant='tertiary'
                            className='tw:text-neutrals-800 tw:dark:text-neutrals-500 tw:hover:text-neutrals-1200 tw:dark:hover:text-white'
                            {...props}
                        />
                    ))}
                    {user?.isLoggedIn && (
                        <MenuButton>
                            <MenuButton.Trigger
                                onClick={() => navigate('/dashboard/security')}
                                data-testid='public-layout-dashboard-button'
                                className='tw:border-transparent tw:dark:border-transparent tw:p-8'
                            >
                                <Icon iconName='dashboard' />
                            </MenuButton.Trigger>
                        </MenuButton>
                    )}
                    {pathname !== '/' && (
                        <MenuButton>
                            <MenuButton.Trigger
                                onClick={() => {
                                    if (canAccessHomePage(user)) {
                                        navigate('/', { state: { fromHomeButton: true } });
                                    } else {
                                        navigate('/dashboard/security');
                                    }
                                }}
                                data-testid='public-layout-home-button'
                                className='tw:border-transparent tw:dark:border-transparent tw:p-8'
                            >
                                <Icon iconName='home' />
                            </MenuButton.Trigger>
                        </MenuButton>
                    )}
                    <PageHeader />
                </div>
            </header>
            <main className='tw:w-full tw:flex tw:flex-col tw:flex-1 tw:overflow-y-auto tw:overflow-x-hidden'>
                <div className='tw:flex-1'>{children}</div>
                <Footer />
            </main>
        </div>
    );
};
